import { fontAttrs } from "@/components/tools/printables/page-kit";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { type TextMeasure, textWidthMm } from "@/lib/tool-kit/printables/text";

const TITLE_FONT = { kind: "sans", bold: true } as const;
const LABEL_FONT = { kind: "sans" } as const;
export const INK = "#1a1a1a";
export const MUTED = "#666666";

const LABEL_SIZE = 4;
const TITLE_SIZE = 7;

export interface HeaderField {
  label: string;
  /** Share of the row's width, out of the total of all shares. */
  share: number;
}

/**
 * A title and, under it, a row of labelled write-in lines (Name, Date, Score). Takes
 * `spellingHeaderHeight`-style space at the top of a page: 11 mm for the title and 10 mm
 * for the row.
 */
export function SheetHeader({
  geometry,
  title,
  fields,
  measure,
}: {
  geometry: PageGeometry;
  title: string;
  fields: readonly HeaderField[];
  measure: TextMeasure;
}) {
  const left = geometry.left;
  const width = geometry.contentWidth;
  const totalShare = fields.reduce((sum, field) => sum + field.share, 0) || 1;
  const gap = 6;
  const usable = width - gap * Math.max(0, fields.length - 1);
  const rowY = geometry.top + (title ? 11 + 6 : 6);
  let x = left;
  return (
    <g fill={INK}>
      {title ? (
        <text
          fontSize={TITLE_SIZE}
          x={left}
          y={geometry.top + 6}
          {...fontAttrs(TITLE_FONT)}
        >
          {title}
        </text>
      ) : null}
      {fields.map((field) => {
        const fieldWidth = (usable * field.share) / totalShare;
        const labelWidth = textWidthMm(
          measure,
          field.label,
          LABEL_FONT,
          LABEL_SIZE
        );
        const startX = x;
        x += fieldWidth + gap;
        return (
          <g key={field.label}>
            <text
              fontSize={LABEL_SIZE}
              x={Math.round(startX * 100) / 100}
              y={rowY}
              {...fontAttrs(LABEL_FONT)}
            >
              {field.label}
            </text>
            <line
              stroke={INK}
              strokeWidth={0.25}
              x1={Math.round((startX + labelWidth + 2) * 100) / 100}
              x2={Math.round((startX + fieldWidth) * 100) / 100}
              y1={rowY + 0.8}
              y2={rowY + 0.8}
            />
          </g>
        );
      })}
    </g>
  );
}
